import React, { useState, useEffect } from 'react';
import api from '../services/api';
import axios from 'axios'; // TODO: rimuovere, usare api
import { PlusIcon, MagnifyingGlassIcon, ChevronLeftIcon, ChevronRightIcon, TrashIcon, PencilIcon, EyeIcon, XMarkIcon, CheckIcon, PhotoIcon } from '@heroicons/react/24/outline';

// Componente SearchableSelect personalizzato
const SearchableSelect = ({ options, value, onChange, placeholder, name }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  
  // Aggiorna le opzioni filtrate quando cambiano le opzioni o il termine di ricerca
  useEffect(() => {
    if (!searchTerm) {
      setFilteredOptions(options);
    } else {
      setFilteredOptions(
        options.filter(option => 
          option.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }
  }, [options, searchTerm]);
  
  // Trova l'etichetta corrente in base al valore
  const currentLabel = options.find(option => option.value === value)?.label || placeholder;
  
  return (
    <div className="relative w-full">
      <div 
        className="w-full border border-gray-300 rounded p-2 bg-white cursor-pointer flex justify-between items-center"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={`${value ? 'text-gray-900' : 'text-gray-500'}`}>
          {currentLabel}
        </span>
        <svg className="h-4 w-4 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </div>
      
      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg">
          <div className="p-2 border-b">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Cerca..."
              className="w-full p-1 border border-gray-300 rounded"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.key === 'Escape' && setIsOpen(false)}
              autoFocus
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="p-2 text-gray-500 text-center">Nessun risultato</div>
            ) : (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  className={`p-2 cursor-pointer hover:bg-gray-100 ${value === option.value ? 'bg-blue-50 text-blue-700' : ''}`}
                  onClick={() => {
                    onChange({ target: { name, value: option.value } });
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                >
                  {option.label}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const VehicleList = () => {
  const [vehicles, setVehicles] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState('veicoli'); // 'veicoli' o 'immagini'
  const [searchQuery, setSearchQuery] = useState('');
  const [totalResults, setTotalResults] = useState(0);
  const [sortBy, setSortBy] = useState('');
  const [immagini, setImmagini] = useState([]);
  const [imageLoading, setImageLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [vehicleFilter, setVehicleFilter] = useState({
    marca_id: '',
    modello: ''
  });
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [imageToAssign, setImageToAssign] = useState(null);
  const [imageSearch, setImageSearch] = useState("");
  const [filteredImages, setFilteredImages] = useState([]);
  const [showVehicleIds, setShowVehicleIds] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    type: 'success', // 'success', 'error', 'warning', 'info'
    timeout: null
  });

  // Stati per la modale di creazione veicolo
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newVehicle, setNewVehicle] = useState({
    id_marca: '',
    modello: '',
    allestimento: '',
    brand: '',
    id_categoria: '',
    id_tipo: '',
    alimentazione: '',
    trasmissione: '',
    "engine_capacity (CC)": '',
    potenza: '',
    prezzo_listino: '',
    note_aggiuntive: ''
  });
  const [creatingVehicle, setCreatingVehicle] = useState(false);

  // Stati per le azioni sui veicoli
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingVehicle, setViewingVehicle] = useState(null);
  const [deletingVehicle, setDeletingVehicle] = useState(null);

  // Stato dei filtri
  const [filters, setFilters] = useState({
    search: '',
    marca_id: '',
    categoria_id: '',
    tipo_id: '',
    alimentazione: '',
    trasmissione: '',
    prezzo_min: '',
    prezzo_max: ''
  });

  useEffect(() => {
    fetchVehicles();
    fetchBrands();
    fetchCategories();
    fetchTypes();
  }, [currentPage, filters]);

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      
      // Costruisci i parametri di query dai filtri
      const params = {
        page: currentPage,
        limit: 10,
        ...filters
      };
      
      console.log("Fetching vehicles with params:", params);
      const response = await api.get('/vehicles', { params });
      console.log("API response:", response.data);
      
      const vehicles = response.data.vehicles || [];
      console.log("Vehicles received:", vehicles.length);
      if (vehicles.length > 0) {
        console.log("First vehicle:", vehicles[0]);
      }
      
      setVehicles(vehicles);
      setTotalPages(response.data.totalPages || 1);
      setCurrentPage(response.data.currentPage || 1);
      setTotalResults(response.data.total || 0);
      setLoading(false);
    } catch (err) {
      console.error('Errore nel recupero dei veicoli:', err);
      setError('Si è verificato un errore nel caricamento dei veicoli.');
      setLoading(false);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await api.get('/vehicles/brands');
      const data = response.data;
      // Assicuriamoci di avere sempre un array
      if (Array.isArray(data)) {
        setBrands(data);
      } else if (data && Array.isArray(data.data)) {
        setBrands(data.data);
      } else {
        console.warn('Formato dati brands non valido:', data);
        setBrands([]);
      }
    } catch (err) {
      console.error('Errore nel recupero dei marchi:', err);
      setBrands([]);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/vehicles/categories');
      const data = response.data;
      // Assicuriamoci di avere sempre un array
      if (Array.isArray(data)) {
        setCategories(data);
      } else if (data && Array.isArray(data.data)) {
        setCategories(data.data);
      } else {
        console.warn('Formato dati categories non valido:', data);
        setCategories([]);
      }
    } catch (err) {
      console.error('Errore nel recupero delle categorie:', err);
      setCategories([]);
    }
  };

  const fetchTypes = async () => {
    try {
      const response = await api.get('/vehicles/types');
      const data = response.data;
      // Assicuriamoci di avere sempre un array
      if (Array.isArray(data)) {
        setTypes(data);
      } else if (data && Array.isArray(data.data)) {
        setTypes(data.data);
      } else {
        console.warn('Formato dati types non valido:', data);
        setTypes([]);
      }
    } catch (err) {
      console.error('Errore nel recupero dei tipi:', err);
      setTypes([]);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Resetta la pagina a 1 ogni volta che cambia un filtro
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const resetFilters = () => {
    setFilters({
      search: '',
      marca_id: '',
      categoria_id: '',
      tipo_id: '',
      alimentazione: '',
      trasmissione: '',
      prezzo_min: '',
      prezzo_max: ''
    });
    setCurrentPage(1);
  };

  // Funzione per gestire la ricerca
  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchQuery) {
      return;
    }
    
    setFilters(prev => ({
      ...prev,
      search: searchQuery
    }));
    setCurrentPage(1); // Torna alla prima pagina quando fai una ricerca
  };

  // Definizione delle colonne della tabella
  const columns = [
    ...(showVehicleIds ? [{ id: 'id', label: 'ID', sortable: true }] : []),
    { id: 'immagine', label: 'IMMAGINE', sortable: false },
    { id: 'marca', label: 'MARCA', sortable: true },
    { id: 'modello', label: 'MODELLO', sortable: true },
    { id: 'allestimento', label: 'ALLESTIMENTO', sortable: true },
    { id: 'tipo', label: 'TIPO', sortable: true },
    { id: 'categoria', label: 'CATEGORIA', sortable: true },
    { id: 'alimentazione', label: 'ALIMENTAZIONE', sortable: true },
    { id: 'trasmissione', label: 'TRASMISSIONE', sortable: true },
    { id: 'prezzo', label: 'PREZZO', sortable: true }
  ];

  // Ordinare i veicoli
  const handleSort = (columnId) => {
    if (!columnId || !vehicles.length) {
      return;
    }
    
    const isAsc = sortBy === columnId;
    const newSortBy = isAsc ? `-${columnId}` : columnId;
    setSortBy(newSortBy);
    
    // Invia la richiesta con il nuovo ordinamento
    setFilters(prev => ({
      ...prev,
      sort_by: columnId,
      sort_order: isAsc ? 'DESC' : 'ASC'
    }));
  };

  // Fetch della lista di immagini
  const fetchImmagini = async () => {
    try {
      setImageLoading(true);
      const response = await api.get('/vetture/immagini');
      setImmagini(response.data || []);
      setImageLoading(false);
    } catch (err) {
      console.error('Errore nel recupero delle immagini:', err);
      setImageLoading(false);
    }
  };

  // Funzione per ottenere le vetture filtrate
  const fetchFilteredVehicles = async () => {
    try {
      const params = { ...vehicleFilter };
      const response = await api.get('/vetture/immagini/filtro', { params });
      setFilteredVehicles(response.data || []);
    } catch (err) {
      console.error('Errore nel recupero delle vetture filtrate:', err);
    }
  };

  // Gestione upload immagini
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Controlla le estensioni
      const validExtensions = ['jpg', 'jpeg', 'png'];
      const fileExtension = file.name.split('.').pop().toLowerCase();
      
      if (!validExtensions.includes(fileExtension)) {
        alert('Per favore seleziona un\'immagine in formato JPG o PNG');
        return;
      }

      setUploadedFile(file);
      // Anteprima dell'immagine
      const reader = new FileReader();
      reader.onload = (e) => {
        setSelectedImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Funzione per mostrare una notifica
  const showNotification = (message, type = 'success', duration = 3000) => {
    // Clear any existing timeout
    if (notification.timeout) {
      clearTimeout(notification.timeout);
    }
    
    // Set the notification
    setNotification({
      show: true,
      message,
      type,
      timeout: setTimeout(() => {
        setNotification(prev => ({ ...prev, show: false }));
      }, duration)
    });
  };

  // Funzione per caricare l'immagine
  const handleUpload = async () => {
    if (!uploadedFile) return;
    
    const formData = new FormData();
    formData.append('immagine', uploadedFile);
    
    try {
      setUploadProgress(10);
      
      const response = await api.post('/vetture/immagini/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });
      
      if (response.data && response.data.immagine) {
        // Aggiungi la nuova immagine alla lista
        const nuovaImmagine = response.data.immagine;
        
        // Assicurati che l'immagine abbia il nome del file
        if (!nuovaImmagine.fileName) {
          nuovaImmagine.fileName = nuovaImmagine.path_file.split('/').pop();
        }
        
        setImmagini(prev => [nuovaImmagine, ...prev]);
        setFilteredImages(prev => [nuovaImmagine, ...prev]);
        
        // Reset
        setUploadedFile(null);
        setSelectedImage(null);
        setUploadProgress(0);
        
        showNotification('Immagine caricata con successo!', 'success');
      }
    } catch (err) {
      console.error('Errore durante l\'upload dell\'immagine:', err);
      showNotification('Errore durante il caricamento dell\'immagine', 'error');
      setUploadProgress(0);
    }
  };

  // Gestione selezione veicoli
  const handleVehicleSelection = (vehicle) => {
    setSelectedVehicles(prev => {
      const isSelected = prev.some(v => v.id === vehicle.id);
      if (isSelected) {
        return prev.filter(v => v.id !== vehicle.id);
      } else {
        return [...prev, vehicle];
      }
    });
  };

  // Funzione per assegnare un'immagine a veicoli selezionati
  const assignImageToVehicles = async (imageId) => {
    if (selectedVehicles.length === 0) {
      showNotification('Seleziona almeno un veicolo prima di assegnare un\'immagine', 'warning');
      return;
    }
    
    try {
      setImageLoading(true);
      const vehicleIds = selectedVehicles.map(v => v.id);
      
      await api.post('/vetture/immagini/assign', {
        imageId,
        vehicleIds
      });
      
      // Aggiorna le vetture filtrate per riflettere l'assegnazione dell'immagine
      const updatedVehicles = filteredVehicles.map(vehicle => {
        if (vehicleIds.includes(vehicle.id)) {
          // Trova l'immagine dal suo ID
          const assignedImage = immagini.find(img => img.id === imageId);
          return {
            ...vehicle,
            img: imageId,
            immagine: assignedImage
          };
        }
        return vehicle;
      });
      
      setFilteredVehicles(updatedVehicles);
      
      // Aggiorna la lista delle immagini per vedere l'utilizzo aggiornato
      fetchImmagini();
      
      // Resetta lo stato dell'UI
      setImageToAssign(null);
      
      // Reset completo dei filtri e della selezione dopo l'assegnazione
      setVehicleFilter({marca_id: '', modello: ''});
      setSelectedVehicles([]);
      setFilteredVehicles([]);
      
      showNotification(`Immagine assegnata con successo a ${vehicleIds.length} veicoli`, 'success');
      setImageLoading(false);
    } catch (err) {
      console.error('Errore durante l\'assegnazione dell\'immagine:', err);
      showNotification('Errore durante l\'assegnazione dell\'immagine', 'error');
      setImageLoading(false);
    }
  };

  // Funzione per eliminare un'immagine
  const deleteImage = async (imageId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa immagine? L\'eliminazione è permanente.')) {
      return;
    }
    
    try {
      await api.delete(`/vetture/immagini/${imageId}`);
      
      // Rimuovi l'immagine dalla lista
      setImmagini(prev => prev.filter(img => img.id !== imageId));
      setFilteredImages(prev => prev.filter(img => img.id !== imageId));
      
      showNotification('Immagine eliminata con successo', 'success');
    } catch (err) {
      console.error('Errore durante l\'eliminazione dell\'immagine:', err);
      showNotification('Errore durante l\'eliminazione dell\'immagine', 'error');
    }
  };

  // Carica immagini quando si attiva il tab
  useEffect(() => {
    if (activeTab === 'immagini') {
      fetchImmagini();
    }
  }, [activeTab]);

  // Aggiorna le vetture filtrate quando cambia il filtro
  useEffect(() => {
    if (activeTab === 'immagini' && (vehicleFilter.marca_id || vehicleFilter.modello)) {
      fetchFilteredVehicles();
    }
  }, [vehicleFilter, activeTab]);

  // Filtra le immagini in base alla ricerca
  const handleImageSearch = (e) => {
    const search = e.target.value.toLowerCase();
    setImageSearch(search);
    
    if (!search.trim()) {
      setFilteredImages(immagini);
      return;
    }
    
    const filtered = immagini.filter(img => {
      const fileName = img.fileName || img.path_file.split('/').pop();
      return fileName.toLowerCase().includes(search);
    });
    
    setFilteredImages(filtered);
  };

  // Prepara l'assegnazione dell'immagine ai veicoli
  const prepareImageAssignment = (img) => {
    setImageToAssign(img);
    // Se non ci sono veicoli selezionati, mostra un messaggio
    if (selectedVehicles.length === 0) {
      showNotification('Seleziona i veicoli prima di assegnare l\'immagine', 'warning');
    }
  };

  // Quando si cambia tab, resetta lo stato di assegnazione immagine
  useEffect(() => {
    setImageToAssign(null);
    setSelectedVehicles([]);
  }, [activeTab]);

  // Quando vengono caricate le immagini, imposta anche le immagini filtrate
  useEffect(() => {
    setFilteredImages(immagini);
  }, [immagini]);

  // Aggiungi questa funzione per costruire l'URL dell'immagine da qualsiasi proprietà che arriva
  const getImageUrl = (vehicle) => {
    if (!vehicle.img && !vehicle.immagine) return null;
    
    // Se abbiamo l'oggetto immagine completo, usa il suo path
    if (vehicle.immagine && vehicle.immagine.path_file) {
      return `${window.location.origin}${vehicle.immagine.path_file}`;
    }
    
    // Fallback per compatibilità: se img è un numero, è probabilmente un ID
    if (typeof vehicle.img === 'number') {
      return null; // Non possiamo mostrare un'immagine usando solo l'ID
    }
    
    // Se img è un path, usalo (caso legacy)
    if (typeof vehicle.img === 'string' && vehicle.img.startsWith('/')) {
      return `${window.location.origin}${vehicle.img}`;
    }
    
    return null;
  };

  // Funzioni per la modale di creazione veicolo
  const handleNewVehicleChange = (e) => {
    const { name, value } = e.target;
    setNewVehicle(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditVehicleChange = (e) => {
    const { name, value } = e.target;
    setEditingVehicle(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetNewVehicle = () => {
    setNewVehicle({
      id_marca: '',
      modello: '',
      allestimento: '',
      brand: '',
      id_categoria: '',
      id_tipo: '',
      alimentazione: '',
      trasmissione: '',
      "engine_capacity (CC)": '',
      potenza: '',
      prezzo_listino: '',
      note_aggiuntive: ''
    });
  };

  const handleCreateVehicle = async () => {
    try {
      setCreatingVehicle(true);
      
      const response = await api.post('/vehicles', newVehicle);
      
      showNotification('Veicolo creato con successo!', 'success');
      setShowCreateModal(false);
      resetNewVehicle();
      
      // Ricarica la lista dei veicoli
      fetchVehicles();
    } catch (error) {
      console.error('Errore nella creazione del veicolo:', error);
      showNotification(
        'Errore nella creazione del veicolo: ' + (error.response?.data?.message || error.message), 
        'error'
      );
    } finally {
      setCreatingVehicle(false);
    }
  };

  // Funzioni per le azioni sui veicoli
  const handleViewVehicle = (vehicle) => {
    setViewingVehicle(vehicle);
    setShowViewModal(true);
  };

  const handleEditVehicle = (vehicle) => {
    setEditingVehicle({
      id: vehicle.id,
      id_marca: vehicle.id_marca?.toString() || '',
      modello: vehicle.modello || '',
      allestimento: vehicle.allestimento || '',
      brand: vehicle.brand || '',
      id_categoria: vehicle.id_categoria?.toString() || '',
      id_tipo: vehicle.id_tipo?.toString() || '',
      alimentazione: vehicle.alimentazione || '',
      trasmissione: vehicle.trasmissione || '',
      "engine_capacity (CC)": vehicle["engine_capacity (CC)"] || '',
      potenza: vehicle.potenza || '',
      prezzo_listino: vehicle.prezzo_listino || '',
      note_aggiuntive: vehicle.note_aggiuntive || ''
    });
    setShowEditModal(true);
  };

  const handleUpdateVehicle = async () => {
    try {
      setCreatingVehicle(true);
      
      const response = await api.put(`/vehicles/${editingVehicle.id}`, editingVehicle);
      
      showNotification('Veicolo aggiornato con successo!', 'success');
      setShowEditModal(false);
      setEditingVehicle(null);
      
      // Ricarica la lista dei veicoli
      fetchVehicles();
    } catch (error) {
      console.error('Errore nell\'aggiornamento del veicolo:', error);
      showNotification(
        'Errore nell\'aggiornamento del veicolo: ' + (error.response?.data?.message || error.message), 
        'error'
      );
    } finally {
      setCreatingVehicle(false);
    }
  };

  const handleDeleteVehicle = async (vehicle) => {
    if (!confirm(`Sei sicuro di voler eliminare il veicolo "${vehicle.modello}"?`)) {
      return;
    }

    try {
      setDeletingVehicle(vehicle.id);
      
      await api.delete(`/vehicles/${vehicle.id}`);
      
      showNotification('Veicolo eliminato con successo!', 'success');
      
      // Ricarica la lista dei veicoli
      fetchVehicles();
    } catch (error) {
      console.error('Errore nell\'eliminazione del veicolo:', error);
      showNotification(
        'Errore nell\'eliminazione del veicolo: ' + (error.response?.data?.message || error.message), 
        'error'
      );
    } finally {
      setDeletingVehicle(null);
    }
  };

  // Trasforma brands in formato utilizzabile dalle SearchableSelect
  const brandOptions = [
    { value: '', label: 'Tutte le marche' },
    ...(Array.isArray(brands) ? brands.map(brand => ({ value: brand.id.toString(), label: brand.descrizione })) : [])
  ];
  
  // Trasforma categories in formato utilizzabile dalle SearchableSelect
  const categoryOptions = [
    { value: '', label: 'Tutte le categorie' },
    ...(Array.isArray(categories) ? categories.map(category => ({ value: category.id.toString(), label: category.descrizione })) : [])
  ];
  
  // Opzioni per il carburante
  const fuelOptions = [
    { value: '', label: 'Tutti i carburanti' },
    { value: 'Benzina', label: 'Benzina' },
    { value: 'Benzina GPL', label: 'Benzina GPL' },
    { value: 'Diesel', label: 'Diesel' },
    { value: 'Elettrica', label: 'Elettrica' },
    { value: 'Ibrida Benzina', label: 'Ibrida Benzina' },
    { value: 'Gasoline', label: 'Benzina (Gasoline)' },
    { value: 'LGP', label: 'GPL' },
    { value: 'GPL', label: 'GPL' }
  ];

  // Trasforma types in formato utilizzabile dalle SearchableSelect
  const typeOptions = [
    { value: '', label: 'Tutti i tipi' },
    ...(Array.isArray(types) ? types.map(type => ({ value: type.id.toString(), label: type.descrizione })) : [])
  ];

  // Opzioni per la trasmissione
  const transmissionOptions = [
    { value: '', label: 'Seleziona cambio' },
    { value: 'Manuale', label: 'Manuale' },
    { value: 'manuale', label: 'Manuale (minuscolo)' },
    { value: 'Automatico', label: 'Automatico' },
    { value: 'automatico', label: 'Automatico (minuscolo)' },
    { value: 'automatico con modalità manuale', label: 'Automatico con modalità manuale' },
    { value: 'Robotizzato', label: 'Robotizzato' },
    { value: 'CVT', label: 'CVT' },
    { value: 'doppia frizione sequenziale automatica', label: 'Doppia frizione sequenziale automatica' }
  ];

  // Rendering condizionale durante il caricamento
  if (loading && vehicles.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Rendering in caso di errore
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative" role="alert">
        <strong className="font-bold">Errore: </strong>
        <span className="block sm:inline">{error}</span>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 py-4">
      {/* Notifiche */}
      {notification.show && (
        <div 
          className={`fixed top-4 right-4 z-50 shadow-lg rounded-md px-4 py-3 border-l-4 flex items-start transition-all duration-300 animate-slide-in-right max-w-md ${
            notification.type === 'success' ? 'bg-green-50 text-green-800 border-green-500' :
            notification.type === 'error' ? 'bg-red-50 text-red-800 border-red-500' :
            notification.type === 'warning' ? 'bg-yellow-50 text-yellow-800 border-yellow-500' :
            'bg-blue-50 text-blue-800 border-blue-500'
          }`}
        >
          {/* Icona in base al tipo */}
          <div className="mr-3 flex-shrink-0">
            {notification.type === 'success' && (
              <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            )}
            {notification.type === 'error' && (
              <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
            {notification.type === 'warning' && (
              <svg className="h-5 w-5 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            )}
            {notification.type === 'info' && (
              <svg className="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
          </div>
          
          {/* Messaggio */}
          <div className="flex-1">
            <p className="font-medium">{notification.message}</p>
          </div>
          
          {/* Pulsante per chiudere */}
          <button 
            className="ml-4 flex-shrink-0 text-gray-400 hover:text-gray-600"
            onClick={() => setNotification(prev => ({ ...prev, show: false }))}
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      <div className="max-w-[1800px] mx-auto">
        {/* Breadcrumb */}
        <nav className="flex mb-6 text-sm" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <a href="/nlt/" className="inline-flex items-center text-blue-600 hover:text-blue-800">
                Dashboard
              </a>
            </li>
            <li aria-current="page">
              <div className="flex items-center">
                <svg className="w-3 h-3 text-gray-400 mx-1" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 6 10">
                  <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 9 4-4-4-4"/>
                </svg>
                <span className="ml-1 text-gray-500 md:ml-2">Lista veicoli</span>
              </div>
            </li>
          </ol>
        </nav>

        {/* Tabs per Veicoli e Immagini */}
        <div className="border-b border-gray-200 mb-4">
          <div className="flex">
            <button
              className={`py-2 px-4 border-b-2 ${activeTab === 'veicoli' ? 'border-blue-500 text-blue-600 font-medium' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
              onClick={() => setActiveTab('veicoli')}
            >
              Veicoli
            </button>
            <button
              className={`py-2 px-4 border-b-2 ${activeTab === 'immagini' ? 'border-blue-500 text-blue-600 font-medium' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
              onClick={() => setActiveTab('immagini')}
            >
              Immagini Vetture
            </button>
          </div>
        </div>

        {/* Contenuto condizionale in base alla tab attiva */}
        {activeTab === 'veicoli' ? (
          <>
            {/* Titolo e pulsante Nuovo Veicolo */}
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-2xl font-bold text-gray-900">Lista Veicoli</h1>
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Nuovo Veicolo
              </button>
            </div>

            {/* Barra di ricerca e filtri */}
            <div className="flex flex-wrap gap-4 mb-6">
              <div className="flex-1 flex space-x-2">
                <button
                  className="p-2 flex items-center justify-center bg-white border border-gray-300 rounded hover:bg-gray-50"
                  onClick={resetFilters}
                  title="Resetta filtri"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 4V9H4.582L9 14.8476V19.6H15V14.8571L19.418 9H20V4H4Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <button
                  className={`p-2 flex items-center justify-center ${showVehicleIds ? 'bg-blue-100 border border-blue-300' : 'bg-white border border-gray-300'} rounded hover:bg-gray-50`}
                  onClick={() => setShowVehicleIds(!showVehicleIds)}
                  title={showVehicleIds ? "Nascondi ID veicoli" : "Mostra ID veicoli"}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                  </svg>
                </button>
                <div className="flex-1 relative">
                  <form onSubmit={handleSearch}>
                    <input
                      type="text"
                      placeholder="Cerca per modello, marca, categoria, ecc."
                      className="w-full p-2 border border-gray-300 rounded"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </form>
                </div>
                <button
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  onClick={handleSearch}
                >
                  Cerca
                </button>
              </div>
              
              {/* Filtri aggiuntivi per marche con ricerca */}
              <div className="w-48">
                <SearchableSelect
                  options={brandOptions}
                  value={filters.marca_id}
                  onChange={handleFilterChange}
                  placeholder="Tutte le marche"
                  name="marca_id"
                />
              </div>
              
              {/* Filtri aggiuntivi per categorie con ricerca */}
              <div className="w-48">
                <SearchableSelect
                  options={categoryOptions}
                  value={filters.categoria_id}
                  onChange={handleFilterChange}
                  placeholder="Tutte le categorie"
                  name="categoria_id"
                />
              </div>
              
              {/* Filtri aggiuntivi per carburante con ricerca */}
              <div className="w-48">
                <SearchableSelect
                  options={fuelOptions}
                  value={filters.alimentazione}
                  onChange={handleFilterChange}
                  placeholder="Tutti i carburanti"
                  name="alimentazione"
                />
              </div>
              
              {/* Filtri aggiuntivi per trasmissione con ricerca */}
              <div className="w-48">
                <SearchableSelect
                  options={transmissionOptions}
                  value={filters.trasmissione}
                  onChange={handleFilterChange}
                  placeholder="Tutti i cambi"
                  name="trasmissione"
                />
              </div>
            </div>

            {/* Numero totale di risultati */}
            <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-200 mb-4">
              {totalResults} RISULTATI TOTALI
            </div>

            {/* Tabella dei veicoli */}
            <div className="bg-white shadow rounded-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {columns.map(column => (
                        <th
                          key={column.id}
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                          onClick={() => column.sortable && handleSort(column.id)}
                        >
                          <div className="flex items-center">
                            {column.label}
                            {column.sortable && (
                              <svg
                                className={`ml-1 w-4 h-4 ${sortBy === column.id ? 'text-blue-600' : 'text-gray-400'}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                              </svg>
                            )}
                          </div>
                        </th>
                      ))}
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Azioni
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {loading ? (
                      <tr>
                        <td colSpan={columns.length + 1} className="px-6 py-4 text-center">
                          <div className="flex justify-center items-center">
                            <svg className="animate-spin h-5 w-5 mr-3 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Caricamento veicoli...
                          </div>
                        </td>
                      </tr>
                    ) : vehicles.length === 0 ? (
                      <tr>
                        <td colSpan={columns.length + 1} className="px-6 py-4 text-center text-gray-500">
                          Nessun veicolo trovato.
                        </td>
                      </tr>
                    ) : (
                      vehicles.map((vehicle) => (
                        <tr key={vehicle.id} className="bg-white">
                          {showVehicleIds && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                              {vehicle.id}
                            </td>
                          )}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {getImageUrl(vehicle) ? (
                              <img 
                                src={getImageUrl(vehicle)} 
                                alt={`${vehicle.marca?.descrizione || vehicle.brand} ${vehicle.modello}`}
                                className="h-12 w-auto object-contain"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='60'%3E%3Crect width='100' height='60' fill='%23e5e7eb'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='10' fill='%239ca3af'%3ENo Image%3C/text%3E%3C/svg%3E";
                                }}
                              />
                            ) : (
                              <div className="h-12 w-12 bg-gray-200 flex items-center justify-center text-gray-500">
                                <PhotoIcon className="h-6 w-6 text-gray-400" />
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {vehicle.marca?.descrizione || vehicle.brand || '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {vehicle.modello || '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {vehicle.allestimento || '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {vehicle.tipo?.descrizione || '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {vehicle.categoria?.descrizione || '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {vehicle.alimentazione || '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {vehicle.trasmissione || '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {vehicle.prezzo_listino 
                              ? parseFloat(vehicle.prezzo_listino).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' }) 
                              : '-'
                            }
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button 
                              onClick={() => handleViewVehicle(vehicle)}
                              className="text-blue-600 hover:text-blue-900 mr-3"
                            >
                              Visualizza
                            </button>
                            <button 
                              onClick={() => handleEditVehicle(vehicle)}
                              className="text-green-600 hover:text-green-900 mr-3"
                            >
                              Modifica
                            </button>
                            <button 
                              onClick={() => handleDeleteVehicle(vehicle)}
                              disabled={deletingVehicle === vehicle.id}
                              className={`${deletingVehicle === vehicle.id ? 'text-gray-400 cursor-not-allowed' : 'text-red-600 hover:text-red-900'}`}
                            >
                              {deletingVehicle === vehicle.id ? 'Eliminazione...' : 'Elimina'}
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Paginazione */}
            {totalPages > 1 && (
              <div className="flex justify-between items-center mt-4">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-4 py-2 border border-gray-300 rounded-md text-sm ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:bg-gray-50'}`}
                >
                  Precedente
                </button>
                <div className="text-sm text-gray-700">
                  Pagina <span className="font-medium">{currentPage}</span> di <span className="font-medium">{totalPages}</span>
                </div>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`px-4 py-2 border border-gray-300 rounded-md text-sm ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:bg-gray-50'}`}
                >
                  Successiva
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="bg-white shadow rounded-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-gray-900">Gestione Immagini Vetture</h2>
              <button
                onClick={async () => {
                  try {
                    setImageLoading(true);
                    const response = await api.post('/vetture/immagini/sync-folder');
                    alert(`Sincronizzazione completata! Trovate ${response.data.stats.totalFilesInFolder} immagini, aggiunte ${response.data.stats.newImagesAdded} nuove immagini, rimosse ${response.data.stats.orphanedImagesRemoved} immagini orfane.`);
                    setImmagini(response.data.images);
                    setImageLoading(false);
                  } catch (err) {
                    console.error('Errore durante la sincronizzazione delle immagini:', err);
                    alert('Errore durante la sincronizzazione delle immagini');
                    setImageLoading(false);
                  }
                }}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                Sincronizza da cartella
              </button>
            </div>
            
            {/* Upload e preview immagine */}
            <div className="mb-6 p-4 border border-gray-200 rounded-md">
              <h3 className="text-md font-medium mb-2">Carica nuova immagine</h3>
              <div className="flex flex-wrap items-start gap-4">
                <div className="w-full md:w-1/3">
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    onChange={handleFileChange}
                    className="block w-full text-sm text-gray-500
                      file:mr-4 file:py-2 file:px-4
                      file:rounded file:border-0
                      file:text-sm file:font-medium
                      file:bg-blue-50 file:text-blue-700
                      hover:file:bg-blue-100"
                  />
                  <p className="mt-1 text-xs text-gray-500">Formati supportati: JPG, PNG</p>
                  
                  {uploadProgress > 0 && (
                    <div className="w-full mt-2">
                      <div className="bg-gray-200 rounded-full h-2.5">
                        <div 
                          className="bg-blue-600 h-2.5 rounded-full" 
                          style={{ width: `${uploadProgress}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{uploadProgress}% caricato</p>
                    </div>
                  )}
                  
                  <button
                    onClick={handleUpload}
                    disabled={!uploadedFile}
                    className="mt-3 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    Carica immagine
                  </button>
                </div>
                
                <div className="w-full md:w-1/3">
                  {selectedImage ? (
                    <div className="border border-gray-200 rounded-md p-2">
                      <img 
                        src={selectedImage} 
                        alt="Anteprima" 
                        className="max-h-32 max-w-full object-contain mx-auto"
                      />
                      <p className="text-xs text-center mt-2">Anteprima dell'immagine</p>
                    </div>
                  ) : (
                    <div className="border border-gray-200 rounded-md p-2 flex items-center justify-center h-32">
                      <PhotoIcon className="h-10 w-10 text-gray-300" />
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Filtro veicoli per associazione */}
            <div className="mb-6 p-4 border border-gray-200 rounded-md">
              <h3 className="text-md font-medium mb-2">Filtra veicoli</h3>
              <div className="flex flex-wrap gap-4">
                <div className="w-48">
                  <SearchableSelect
                    options={brandOptions}
                    value={vehicleFilter.marca_id}
                    onChange={(e) => setVehicleFilter({...vehicleFilter, marca_id: e.target.value})}
                    placeholder="Tutte le marche"
                    name="marca_id"
                  />
                </div>
                
                <input
                  type="text"
                  placeholder="Modello"
                  className="border border-gray-300 rounded p-2"
                  value={vehicleFilter.modello}
                  onChange={(e) => setVehicleFilter({...vehicleFilter, modello: e.target.value})}
                />
                
                <button
                  onClick={fetchFilteredVehicles}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Filtra
                </button>
                
                <button
                  onClick={() => {
                    setVehicleFilter({marca_id: '', modello: ''});
                    setFilteredVehicles([]);
                    setSelectedVehicles([]);
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
                >
                  Reset
                </button>
              </div>
              
              {/* Lista veicoli filtrati con etichette per la selezione */}
              {filteredVehicles.length > 0 && (
                <div className="mt-4">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="text-sm font-medium">Veicoli trovati: {filteredVehicles.length}</h4>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">
                        {selectedVehicles.length} veicoli selezionati
                      </span>
                      {selectedVehicles.length > 0 && (
                        <button
                          onClick={() => setSelectedVehicles([])}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          Deseleziona tutti
                        </button>
                      )}
                      {selectedVehicles.length === 0 && filteredVehicles.length > 0 && (
                        <button
                          onClick={() => setSelectedVehicles([...filteredVehicles])}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          Seleziona tutti
                        </button>
                      )}
                    </div>
                  </div>
                  
                  <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-md">
                    {filteredVehicles.map(vehicle => {
                      const isSelected = selectedVehicles.some(v => v.id === vehicle.id);
                      return (
                        <div 
                          key={vehicle.id} 
                          className={`p-2 text-sm border-b border-gray-200 cursor-pointer flex items-center ${
                            isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
                          }`}
                          onClick={() => handleVehicleSelection(vehicle)}
                        >
                          <div className="flex-1">
                            {showVehicleIds && (
                              <span className="mr-2 text-xs text-gray-500 font-mono">
                                ID: {vehicle.id}
                              </span>
                            )}
                            <span className="font-medium">{vehicle.marca?.descrizione || vehicle.brand}</span>{' '}
                            {vehicle.modello} {vehicle.allestimento ? `- ${vehicle.allestimento}` : ''}
                            
                            {/* Mostra se il veicolo ha già un'immagine assegnata */}
                            {vehicle.immagine && (
                              <span className="ml-2 text-xs text-gray-500">
                                (Immagine: {vehicle.immagine.path_file.split('/').pop()})
                              </span>
                            )}
                          </div>
                          {isSelected ? (
                            <CheckIcon className="h-5 w-5 text-blue-600" />
                          ) : (
                            <div className="h-5 w-5 border border-gray-300 rounded flex-shrink-0"></div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Riassunto e azioni sui veicoli selezionati */}
                  {selectedVehicles.length > 0 && (
                    <div className="mt-2 p-2 bg-blue-50 rounded-md border border-blue-100">
                      <div className="text-sm flex items-center justify-between">
                        <span className="font-medium text-blue-800">
                          {selectedVehicles.length} veicoli selezionati
                        </span>
                        
                        {imageToAssign && (
                          <div className="flex items-center">
                            <span className="text-xs text-blue-700 mr-2">
                              Immagine da assegnare:
                            </span>
                            <div className="h-6 w-6 bg-white rounded overflow-hidden border border-blue-200">
                              <img 
                                src={imageToAssign.fullUrl || `${window.location.origin}${imageToAssign.path_file}`}
                                alt={imageToAssign.fileName}
                                className="h-full w-full object-contain"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Visualizzazione immagini */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-md font-medium">Immagini caricate</h3>
                <div className="flex gap-2">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Cerca immagini..."
                      value={imageSearch}
                      onChange={handleImageSearch}
                      className="pl-8 pr-2 py-1 border border-gray-300 rounded"
                    />
                    <MagnifyingGlassIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  </div>
                  <div className="text-sm text-gray-500 self-center">
                    {filteredImages.length} / {immagini.length} immagini
                  </div>
                </div>
              </div>
              
              {/* Istruzioni chiare per l'assegnazione */}
              <div className="bg-blue-50 p-3 mb-4 rounded-md border border-blue-100">
                <div className="flex items-start">
                  <div className="shrink-0 mt-0.5">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-800">Come assegnare le immagini:</h3>
                    <div className="mt-1 text-xs text-blue-700">
                      <p>1. Filtra e seleziona i veicoli utilizzando i controlli sopra</p>
                      <p>2. Fai clic su <span className="font-medium">Assegna</span> sull'immagine che desideri utilizzare</p>
                      <p>3. Conferma l'assegnazione dell'immagine ai veicoli selezionati</p>
                    </div>
                    
                    {/* Stato dell'operazione di assegnazione */}
                    {selectedVehicles.length > 0 && (
                      <div className="mt-2 p-2 bg-white rounded-md border border-blue-200">
                        <p className="text-xs text-blue-800 font-medium">
                          {imageToAssign 
                            ? `Pronto a assegnare l'immagine "${imageToAssign.fileName || '...'}" a ${selectedVehicles.length} veicoli` 
                            : `${selectedVehicles.length} veicoli selezionati - seleziona un'immagine da assegnare`}
                        </p>
                        
                        {imageToAssign && (
                          <div className="flex gap-2 mt-1">
                            <button
                              onClick={() => assignImageToVehicles(imageToAssign.id)}
                              className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                            >
                              Conferma assegnazione
                            </button>
                            <button
                              onClick={() => setImageToAssign(null)}
                              className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded hover:bg-gray-300"
                            >
                              Annulla
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {imageLoading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
                  <span className="ml-2 text-gray-600">Caricamento immagini...</span>
                </div>
              ) : filteredImages.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <PhotoIcon className="h-12 w-12 mx-auto text-gray-300" />
                  <p className="mt-2">Nessuna immagine trovata</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {filteredImages.map(img => {
                    // Usa il nome del file o ottienilo dal path se non è fornito
                    const fileName = img.fileName || img.path_file.split('/').pop();
                    
                    // Usa l'URL completo se fornito, altrimenti costruisci l'URL dall'origine
                    const imageUrl = img.fullUrl || `${window.location.origin}${img.path_file}`;
                    
                    // Verifica se questa è l'immagine selezionata per l'assegnazione
                    const isSelected = imageToAssign && imageToAssign.id === img.id;
                    
                    return (
                      <div 
                        key={img.id} 
                        className={`border rounded-md p-2 relative group ${isSelected ? 'ring-2 ring-blue-500 bg-blue-50' : 'border-gray-200'}`}
                        onClick={() => prepareImageAssignment(img)}
                      >
                        <div className="h-32 flex items-center justify-center">
                          <img 
                            src={imageUrl} 
                            alt={fileName}
                            className="max-h-32 max-w-full object-contain"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='150' height='150'%3E%3Crect width='150' height='150' fill='%23e5e7eb'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='12' fill='%239ca3af'%3EImmagine non trovata%3C/text%3E%3C/svg%3E";
                            }}
                          />
                        </div>
                        
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="flex space-x-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation(); // Previene il click sul parent
                                prepareImageAssignment(img);
                              }}
                              className="p-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                              title="Assegna alle vetture selezionate"
                            >
                              <CheckIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation(); // Previene il click sul parent
                                deleteImage(img.id);
                              }}
                              className="p-1 bg-red-600 text-white rounded hover:bg-red-700"
                              title="Elimina immagine"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                        
                        <div className="mt-1 text-xs text-center text-gray-500 truncate" title={fileName}>
                          {fileName}
                        </div>
                        
                        {showVehicleIds && (
                          <div className="mt-1 text-xs text-gray-500 text-center font-mono">
                            ID: {img.id}
                          </div>
                        )}
                        
                        {img.usage > 0 && (
                          <div className="mt-1 text-xs text-gray-500 text-center">
                            Usata da {img.usage} vetture
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modale per visualizzare un veicolo */}
      {showViewModal && viewingVehicle && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Dettagli Veicolo</h3>
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    setViewingVehicle(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Marca</label>
                  <p className="text-sm text-gray-900">{viewingVehicle.marca?.descrizione || viewingVehicle.brand || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Modello</label>
                  <p className="text-sm text-gray-900">{viewingVehicle.modello || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Allestimento</label>
                  <p className="text-sm text-gray-900">{viewingVehicle.allestimento || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Brand</label>
                  <p className="text-sm text-gray-900">{viewingVehicle.brand || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
                  <p className="text-sm text-gray-900">{viewingVehicle.categoria?.descrizione || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                  <p className="text-sm text-gray-900">{viewingVehicle.tipo?.descrizione || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Alimentazione</label>
                  <p className="text-sm text-gray-900">{viewingVehicle.alimentazione || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Trasmissione</label>
                  <p className="text-sm text-gray-900">{viewingVehicle.trasmissione || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cilindrata</label>
                  <p className="text-sm text-gray-900">{viewingVehicle["engine_capacity (CC)"] || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Potenza</label>
                  <p className="text-sm text-gray-900">{viewingVehicle.potenza || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prezzo Listino</label>
                  <p className="text-sm text-gray-900">{viewingVehicle.prezzo_listino ? `€${viewingVehicle.prezzo_listino}` : '-'}</p>
                </div>
              </div>

              {viewingVehicle.note_aggiuntive && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                  <p className="text-sm text-gray-900">{viewingVehicle.note_aggiuntive}</p>
                </div>
              )}

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    setViewingVehicle(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Chiudi
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modale per creare un nuovo veicolo */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Nuovo Veicolo</h3>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    resetNewVehicle();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Marca */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Marca *
                  </label>
                  <SearchableSelect
                    options={brandOptions.filter(b => b.value !== '')}
                    value={newVehicle.id_marca}
                    onChange={handleNewVehicleChange}
                    placeholder="Seleziona marca"
                    name="id_marca"
                  />
                </div>

                {/* Modello */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Modello *
                  </label>
                  <input
                    type="text"
                    name="modello"
                    value={newVehicle.modello}
                    onChange={handleNewVehicleChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Es. Golf, A3, Classe A"
                  />
                </div>

                {/* Allestimento */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Allestimento
                  </label>
                  <input
                    type="text"
                    name="allestimento"
                    value={newVehicle.allestimento}
                    onChange={handleNewVehicleChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Es. Comfort, Sport, Executive"
                  />
                </div>

                {/* Brand */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Brand
                  </label>
                  <input
                    type="text"
                    name="brand"
                    value={newVehicle.brand}
                    onChange={handleNewVehicleChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Es. Volkswagen, Audi, BMW"
                  />
                </div>

                {/* Categoria */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Categoria
                  </label>
                  <SearchableSelect
                    options={categoryOptions.filter(c => c.value !== '')}
                    value={newVehicle.id_categoria}
                    onChange={handleNewVehicleChange}
                    placeholder="Seleziona categoria"
                    name="id_categoria"
                  />
                </div>

                {/* Tipo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo
                  </label>
                  <SearchableSelect
                    options={typeOptions.filter(t => t.value !== '')}
                    value={newVehicle.id_tipo}
                    onChange={handleNewVehicleChange}
                    placeholder="Seleziona tipo"
                    name="id_tipo"
                  />
                </div>

                {/* Carburante */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Carburante
                  </label>
                  <SearchableSelect
                    options={fuelOptions.filter(f => f.value !== '')}
                    value={newVehicle.alimentazione}
                    onChange={handleNewVehicleChange}
                    placeholder="Seleziona carburante"
                    name="alimentazione"
                  />
                </div>

                {/* Cambio */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cambio
                  </label>
                  <SearchableSelect
                    options={transmissionOptions.filter(t => t.value !== '')}
                    value={newVehicle.trasmissione}
                    onChange={handleNewVehicleChange}
                    placeholder="Seleziona cambio"
                    name="trasmissione"
                  />
                </div>

                {/* Cilindrata */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cilindrata (cc)
                  </label>
                  <input
                    type="number"
                    name="engine_capacity (CC)"
                    value={newVehicle["engine_capacity (CC)"]}
                    onChange={handleNewVehicleChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Es. 1400, 2000"
                  />
                </div>

                {/* Potenza */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Potenza (cv)
                  </label>
                  <input
                    type="number"
                    name="potenza"
                    value={newVehicle.potenza}
                    onChange={handleNewVehicleChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Es. 90, 150"
                  />
                </div>

                {/* Prezzo Listino */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prezzo Listino (€)
                  </label>
                  <input
                    type="number"
                    name="prezzo_listino"
                    value={newVehicle.prezzo_listino}
                    onChange={handleNewVehicleChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Es. 25000"
                    step="0.01"
                  />
                </div>
              </div>

              {/* Note - a tutta larghezza */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Note
                </label>
                <textarea
                  name="note_aggiuntive"
                  value={newVehicle.note_aggiuntive}
                  onChange={handleNewVehicleChange}
                  rows="3"
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Note aggiuntive..."
                />
              </div>

              {/* Pulsanti */}
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    resetNewVehicle();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Annulla
                </button>
                <button
                  onClick={handleCreateVehicle}
                  disabled={creatingVehicle || !newVehicle.id_marca || !newVehicle.modello}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creatingVehicle ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                      Creazione...
                    </>
                  ) : (
                    'Crea Veicolo'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modale per modificare un veicolo */}
      {showEditModal && editingVehicle && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Modifica Veicolo</h3>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingVehicle(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Marca */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Marca *
                  </label>
                  <SearchableSelect
                    options={brandOptions.filter(b => b.value !== '')}
                    value={editingVehicle.id_marca}
                    onChange={handleEditVehicleChange}
                    placeholder="Seleziona marca"
                    name="id_marca"
                  />
                </div>

                {/* Modello */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Modello *
                  </label>
                  <input
                    type="text"
                    name="modello"
                    value={editingVehicle.modello}
                    onChange={handleEditVehicleChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Es. Golf, A3, Classe A"
                  />
                </div>

                {/* Allestimento */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Allestimento
                  </label>
                  <input
                    type="text"
                    name="allestimento"
                    value={editingVehicle.allestimento}
                    onChange={handleEditVehicleChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Es. Comfort, Sport, Executive"
                  />
                </div>

                {/* Brand */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Brand
                  </label>
                  <input
                    type="text"
                    name="brand"
                    value={editingVehicle.brand}
                    onChange={handleEditVehicleChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Es. Volkswagen, Audi, BMW"
                  />
                </div>

                {/* Categoria */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Categoria
                  </label>
                  <SearchableSelect
                    options={categoryOptions.filter(c => c.value !== '')}
                    value={editingVehicle.id_categoria}
                    onChange={handleEditVehicleChange}
                    placeholder="Seleziona categoria"
                    name="id_categoria"
                  />
                </div>

                {/* Tipo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo
                  </label>
                  <SearchableSelect
                    options={typeOptions.filter(t => t.value !== '')}
                    value={editingVehicle.id_tipo}
                    onChange={handleEditVehicleChange}
                    placeholder="Seleziona tipo"
                    name="id_tipo"
                  />
                </div>

                {/* Carburante */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Carburante
                  </label>
                  <SearchableSelect
                    options={fuelOptions.filter(f => f.value !== '')}
                    value={editingVehicle.alimentazione}
                    onChange={handleEditVehicleChange}
                    placeholder="Seleziona carburante"
                    name="alimentazione"
                  />
                </div>

                {/* Cambio */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cambio
                  </label>
                  <SearchableSelect
                    options={transmissionOptions.filter(t => t.value !== '')}
                    value={editingVehicle.trasmissione}
                    onChange={handleEditVehicleChange}
                    placeholder="Seleziona cambio"
                    name="trasmissione"
                  />
                </div>

                {/* Cilindrata */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cilindrata (cc)
                  </label>
                  <input
                    type="number"
                    name="engine_capacity (CC)"
                    value={editingVehicle["engine_capacity (CC)"]}
                    onChange={handleEditVehicleChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Es. 1400, 2000"
                  />
                </div>

                {/* Potenza */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Potenza (cv)
                  </label>
                  <input
                    type="number"
                    name="potenza"
                    value={editingVehicle.potenza}
                    onChange={handleEditVehicleChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Es. 90, 150"
                  />
                </div>

                {/* Prezzo Listino */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prezzo Listino (€)
                  </label>
                  <input
                    type="number"
                    name="prezzo_listino"
                    value={editingVehicle.prezzo_listino}
                    onChange={handleEditVehicleChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Es. 25000"
                    step="0.01"
                  />
                </div>
              </div>

              {/* Note - a tutta larghezza */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Note
                </label>
                <textarea
                  name="note_aggiuntive"
                  value={editingVehicle.note_aggiuntive}
                  onChange={handleEditVehicleChange}
                  rows="3"
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Note aggiuntive..."
                />
              </div>

              {/* Pulsanti */}
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingVehicle(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Annulla
                </button>
                <button
                  onClick={handleUpdateVehicle}
                  disabled={creatingVehicle || !editingVehicle.id_marca || !editingVehicle.modello}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creatingVehicle ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                      Aggiornamento...
                    </>
                  ) : (
                    'Aggiorna Veicolo'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleList;